import React from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';

interface SectionRetryProps {
  /** Component-level error message (never the generic "no data" copy). */
  message: string;
  onRetry?: () => void;
  /** True while a retry request for this section is in flight. */
  retrying?: boolean;
  /** Optional fixed height so the section keeps its layout footprint. */
  minHeight?: number;
}

/**
 * Component-level failure state: shown *only* inside the single card/section
 * whose own API call failed. Every other card on the tab keeps rendering its
 * own data/loading state untouched.
 */
const SectionRetry: React.FC<SectionRetryProps> = ({ message, onRetry, retrying = false, minHeight = 120 }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  return (
    <View style={[styles.wrap, { minHeight }]}>
      <Text style={styles.message}>{message}</Text>
      {onRetry ? (
        <TouchableOpacity
          style={[styles.retryButton, retrying && styles.retryButtonDisabled, isDark && { borderColor: colors.brand.primary }]}
          onPress={onRetry}
          disabled={retrying}
          accessibilityRole="button"
          accessibilityLabel="Retry"
        >
          <MaterialIcons name="refresh" size={14} color={isDark ? colors.brand.primary : '#1D4ED8'} style={styles.retryIcon} />
          <Text style={[styles.retryText, isDark && { color: colors.brand.primary }]}>{retrying ? 'Retrying…' : 'Retry'}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 12,
  },
  message: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    textAlign: 'center',
    marginBottom: 12,
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
  },
  retryButtonDisabled: {
    opacity: 0.6,
  },
  retryIcon: {
    marginRight: 6,
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
});

export default SectionRetry;
